import { useMemo, useRef, useState, useEffect } from 'react';

interface OptionItem {
  id: string;
  label: string;
}

interface SearchableDropdownProps {
  options: OptionItem[];
  value?: string;
  onChange: (id: string) => void;
  placeholder: string;
}

/** Prefix used for free-typed values that aren't in the options list. */
const FREE_PREFIX = '__free__:';

/** Strip the free-text prefix to get a display label. */
export function freeLabel(id: string) {
  return id.startsWith(FREE_PREFIX) ? id.slice(FREE_PREFIX.length) : id;
}

export function SearchableDropdown({ options, value, onChange, placeholder }: SearchableDropdownProps) {
  const labelOf = (id: string | undefined) => {
    if (!id) return '';
    if (id.startsWith(FREE_PREFIX)) return id.slice(FREE_PREFIX.length);
    return options.find((o) => o.id === id)?.label ?? '';
  };

  const [query, setQuery] = useState(labelOf(value));
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(labelOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const currentLabel = labelOf(value).toLowerCase();
    if (!q || q === currentLabel) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, query, value]);

  /** True when the typed text doesn't exactly match any option label. */
  const hasFreeInput = useMemo(() => {
    const q = query.trim();
    if (!q) return false;
    return !options.some((o) => o.label.toLowerCase() === q.toLowerCase());
  }, [options, query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const select = (opt: OptionItem) => {
    onChange(opt.id);
    setQuery(opt.label);
    setOpen(false);
  };

  /** Accept the current typed value as a free-text answer. */
  const acceptFreeText = () => {
    const text = query.trim();
    if (!text) return;
    onChange(FREE_PREFIX + text);
    setOpen(false);
  };

  // Total items = filtered options + optional "Use '[text]'" row
  const totalItems = filtered.length + (hasFreeInput ? 1 : 0);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        inputMode="search"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(totalItems - 1, h + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[highlight]) {
              select(filtered[highlight]);
            } else if (hasFreeInput) {
              // Highlight is on the free-text row, or no filtered options exist
              acceptFreeText();
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="w-full rounded-2xl border border-ink-light/40 bg-white px-5 py-4 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand-100"
      />
      {open && (filtered.length > 0 || hasFreeInput) && (
        <ul
          role="listbox"
          className="absolute z-10 mt-2 w-full bg-white rounded-2xl border border-ink-light/30 shadow-cardLg max-h-72 overflow-auto"
        >
          {filtered.map((o, i) => (
            <li key={o.id}>
              <button
                type="button"
                role="option"
                aria-selected={highlight === i}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => select(o)}
                className={`w-full text-left px-5 py-3 text-base ${
                  highlight === i ? 'bg-brand-50 text-ink' : 'text-ink'
                }`}
              >
                {o.label}
              </button>
            </li>
          ))}
          {hasFreeInput && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={highlight === filtered.length}
                onMouseEnter={() => setHighlight(filtered.length)}
                onClick={acceptFreeText}
                className={`w-full text-left px-5 py-3 text-base border-t border-ink-light/20 ${
                  highlight === filtered.length
                    ? 'bg-brand-50 text-ink'
                    : 'text-ink-soft'
                }`}
              >
                Use &ldquo;{query.trim()}&rdquo;
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
